// Sprint 3 PR-E5 — Agent Council DB-backed read-only liste.
// Endpoint: GET /api/v2/council/decisions
// Vote button YOK; PR-D2 read-only disiplin korunur.

import { escHtml } from '../utils.js';
import { apiGetJson, postCouncilResolve } from '../api.js';
import { toast } from '../ui/toast.js';
import {
  detectHermesCategory,
  isHermesAuthorityDecision,
  CATEGORY_LABEL,
} from '../lib/hermes-authority.js';
import {
  isRouteRequest,
  routeRequestLifecycle,
  routeRequestFields,
  LIFECYCLE_LABEL,
  LIFECYCLE_TONE,
} from '../lib/hermes-route-request-ui.js';

export interface CouncilDecision {
  id: string;
  tenantId: string;
  decisionType: string;
  subjectType: string;
  subjectId: string;
  actionId: string | null;
  approvalId: string | null;
  context: unknown;
  thresholdRequired: number;
  riskLevel: string;
  status: string;
  outcome: unknown;
  reason: string | null;
  createdByAgent: string | null;
  createdAt: string;
  resolvedAt: string | null;
  votesFor: number;
  votesAgainst: number;
  votesAbstain: number;
  votesTotal: number;
  // Sprint 4 — ağırlıklı toplam (Opus=2/Sonnet=1/Haiku=0.5). Backend list endpoint sağlar.
  weightedFor?: number;
  weightedAgainst?: number;
}

export interface CouncilSummary {
  total: number;
  byStatus: Record<string, number>;
  byRiskLevel: Record<string, number>;
  byDecisionType: Record<string, number>;
}

interface CouncilResponse { ok: true; decisions: CouncilDecision[]; summary: CouncilSummary; }

export interface CouncilFilters {
  status?: string;
  riskLevel?: string;
  decisionType?: string;
  tenantId?: string;
  limit?: number;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; decisions: CouncilDecision[]; summary: CouncilSummary; filters: CouncilFilters }
  | { kind: 'error'; message: string; filters: CouncilFilters };

let state: ViewState = { kind: 'idle' };
let filters: CouncilFilters = { limit: 50 };
// Stream 6 — Hermes Yetki preset filter (frontend-side, backend status filter ile orthogonal).
// Aktifken sadece isHermesAuthorityDecision(d) true olan kararlar tabloya render edilir.
let hermesFilterActive = false;

export async function loadAgentCouncil(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<CouncilResponse>('/api/v2/council/decisions', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', decisions: r.data.decisions, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderAgentCouncil(): void {
  const root = document.getElementById('view-agent-council');
  if (!root) return;
  root.innerHTML = renderAgentCouncilHtml(state);
  attachHandlers();
}

export function renderAgentCouncilHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Agent Council</div>
        <div class="view-sub">central_brain.agent_council_decisions — Sprint 4: ağırlıklı oy (Opus 2 / Sonnet 1 / Haiku 0.5) + Yılmaz onayı belirleyici</div>
      </div>
    </div>
    <div class="rt-card">
      <div class="rt-card-title">Composition (default)</div>
      <ul style="margin:8px 0;color:var(--text2);font-size:13px;">
        <li><strong>Başkan:</strong> Nexus (oy hakkıyla)</li>
        <li><strong>Denetçi:</strong> Onyx (her karar için zorunlu)</li>
        <li><strong>Alan ajanı:</strong> Ruby/Sapphire/Crystal/Zircon/Forge/Pilot/Topaz/Opal (konuya göre)</li>
        <li><strong>Bütçe:</strong> Meter</li>
        <li><strong>Operasyon:</strong> Atlas</li>
      </ul>
    </div>
    <div class="rt-card" style="margin-top:12px;">
      <div class="rt-card-title">Eşikler</div>
      <table style="width:100%;font-size:13px;">
        <thead><tr><th style="text-align:left;">Risk</th><th style="text-align:left;">Eşik</th></tr></thead>
        <tbody>
          <tr><td>low</td><td>2 oy + majority</td></tr>
          <tr><td>medium</td><td>3 oy + majority</td></tr>
          <tr><td>high</td><td>3 oy + majority (+ Sprint 4 insan gate)</td></tr>
          <tr><td>critical</td><td>auto-resolve yok; sadece insan</td></tr>
        </tbody>
      </table>
    </div>
    <div class="rt-card" style="margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('status', 'Status', f.status, ['open','approved','rejected','expired'])}
      ${selectFilter('riskLevel', 'Risk', f.riskLevel, ['low','medium','high','critical'])}
      ${inputFilter('decisionType', 'Decision type', f.decisionType)}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      <button id="cnc-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
      <button id="cnc-hermes-toggle" class="${hermesFilterActive ? 'btn-primary' : ''}" style="padding:6px 12px;border:1px solid var(--border2,#334155);border-radius:4px;background:${hermesFilterActive ? '#1e3a5f' : 'transparent'};color:${hermesFilterActive ? '#9bd0ff' : 'inherit'};cursor:pointer;">🛡️ Hermes Yetki${hermesFilterActive ? ' ✓' : ''}</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { decisions, summary } = s;
  // Stream 6 — Hermes filter preset: aktifken Hermes authority + route-request kararları
  const isHermesRelated = (d: CouncilDecision) => isHermesAuthorityDecision(d) || isRouteRequest(d);
  const filteredDecisions = hermesFilterActive
    ? decisions.filter(isHermesRelated)
    : decisions;
  const hermesCount = decisions.filter(isHermesRelated).length;
  return `${header}
    ${summaryStrip(summary, hermesCount)}
    <div class="rt-card">
      <div class="rt-card-title">Karar Listesi (${filteredDecisions.length}${hermesFilterActive ? ` / ${decisions.length} — sadece Hermes Yetki` : ''})</div>
      ${filteredDecisions.length === 0
        ? '<div class="empty-state"><div class="empty-icon">🗳️</div><div class="empty-title">' + (hermesFilterActive ? 'Hermes Yetki kararı bulunamadı' : 'Henüz council kararı yok') + '</div><div style="color:var(--muted);font-size:12px;">' + (hermesFilterActive ? 'Filtreyi kapat veya farklı status seç.' : 'Vote akışı backend\'de aktif; Sprint 4 escalation gelene kadar manuel yaratılır.') + '</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">ID</th>
              <th style="text-align:left;">Type</th>
              <th style="text-align:left;">Subject</th>
              <th style="text-align:left;">Risk</th>
              <th style="text-align:left;">Status</th>
              <th style="text-align:left;">Votes</th>
              <th style="text-align:left;">Created</th>
              <th style="text-align:left;">Karar (Yılmaz)</th>
            </tr></thead>
            <tbody>${filteredDecisions.map((d) => `
              <tr>
                <td>${escHtml(d.id.slice(0, 8))}</td>
                <td><code style="font-size:11px;">${escHtml(d.decisionType)}</code></td>
                <td style="font-size:11px;color:var(--muted);">${escHtml(d.subjectType)}/${escHtml(d.subjectId.slice(0, 8))}${hermesBadge(d)}</td>
                <td>${escHtml(d.riskLevel)}</td>
                <td>${hermesStatusPillar(d)}</td>
                <td>${voteStrip(d)}</td>
                <td style="color:var(--muted);font-size:12px;">${escHtml(d.createdAt)}</td>
                <td>${resolveActions(d)}</td>
              </tr>
              <tr><td colspan="8" style="padding:0 0 12px 0;">
                <details>
                  <summary style="cursor:pointer;font-size:11px;color:var(--muted);">detay (context + outcome)</summary>
                  <pre style="background:var(--bg2);padding:8px;border-radius:4px;font-size:11px;white-space:pre-wrap;">tenant: ${escHtml(d.tenantId)}
threshold: ${d.thresholdRequired}
reason: ${escHtml(d.reason ?? '-')}
createdBy: ${escHtml(d.createdByAgent ?? '-')}
context: ${escHtml(JSON.stringify(d.context, null, 2))}
outcome: ${escHtml(JSON.stringify(d.outcome, null, 2))}</pre>
                </details>
              </td></tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

function voteStrip(d: CouncilDecision): string {
  const base = 'font-size:11px;padding:2px 6px;border-radius:8px;margin-right:4px;';
  // Ağırlıklı toplam (Opus=2/Sonnet=1/Haiku=0.5) — backend list endpoint sağlar.
  const wf = typeof d.weightedFor === 'number' ? d.weightedFor : null;
  const wTip = wf !== null
    ? `<span style="font-size:11px;color:var(--muted);" title="ağırlıklı For/Against (Opus2/Sonnet1/Haiku0.5)">ağ. ${fmtW(wf)}/${escHtml(String(d.thresholdRequired))}</span>`
    : '';
  return `<span style="${base}background:#2a4a2a;">✓ ${d.votesFor}</span><span style="${base}background:#4a2a2a;">✗ ${d.votesAgainst}</span><span style="${base}background:#3a3a3a;">— ${d.votesAbstain}</span><span style="font-size:11px;color:var(--muted);">/${d.thresholdRequired}</span> ${wTip}`;
}

// Kesirli ağırlık gösterimi (2.5 → "2.5", 3 → "3").
function fmtW(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Sprint 4 — İnsan-override aksiyon hücresi. SADECE open kararda buton; aksi rozet.
// critical dahil her risk insan ile çözülür ("Yılmaz onayı belirleyici").
function resolveActions(d: CouncilDecision): string {
  if (d.status !== 'open') {
    const o = (d.outcome ?? {}) as { resolvedBy?: string };
    const by = o.resolvedBy === 'human' ? 'Yılmaz' : o.resolvedBy === 'weighted-threshold' ? 'ağırlıklı oy' : o.resolvedBy ? escHtml(o.resolvedBy) : '—';
    return `<span style="font-size:11px;color:var(--muted);">${by}</span>`;
  }
  const btn = 'font-size:11px;padding:3px 8px;border-radius:4px;cursor:pointer;border:1px solid var(--border2,#334155);margin-right:4px;';
  const title = (d.context && typeof d.context === 'object' && 'title' in (d.context as Record<string, unknown>))
    ? String((d.context as Record<string, string>).title)
    : d.reason ?? d.decisionType;
  const titleAttr = escHtml(title.slice(0, 120));
  return `<button data-cnc-resolve="approved" data-cnc-id="${escHtml(d.id)}" data-cnc-risk="${escHtml(d.riskLevel)}" data-cnc-title="${titleAttr}" style="${btn}background:#1f4a2a;color:#9be8a8;">✓ Onayla</button>`
    + `<button data-cnc-resolve="rejected" data-cnc-id="${escHtml(d.id)}" data-cnc-risk="${escHtml(d.riskLevel)}" data-cnc-title="${titleAttr}" style="${btn}background:#4a1f1f;color:#f0a8a8;">✕ Reddet</button>`;
}

// Stream 6 — Hermes authority badge (Subject sütununda görünür)
function hermesBadge(d: CouncilDecision): string {
  // Route-request → 🛰️ Hermes→Nexus badge (typed flow)
  if (isRouteRequest(d)) {
    const f = routeRequestFields(d);
    return ` <span title="Hermes → ${escHtml(f.targetAgent)} route-request" style="display:inline-block;font-size:10px;padding:1px 6px;margin-left:4px;border-radius:8px;background:#1e3a5f;color:#9bd0ff;border:1px solid #2563eb;">🛰️ Hermes→${escHtml(f.targetAgent)}</span>`;
  }
  const cat = detectHermesCategory(d);
  if (!cat) return '';
  return ` <span title="${escHtml(CATEGORY_LABEL[cat])}" style="display:inline-block;font-size:10px;padding:1px 6px;margin-left:4px;border-radius:8px;background:#1e3a5f;color:#9bd0ff;border:1px solid #2563eb;">🛡️ ${escHtml(CATEGORY_LABEL[cat])}</span>`;
}

// Stream 6 — status pillar (Hermes kararlarında raw status yerine honest pillar).
// Hermes propose-only — DB-grant capability YOK; approved = council governance
// yetkisi (2026-06-14 düzeltme, bkz. js/lib/hermes-authority.ts).
function hermesStatusPillar(d: CouncilDecision): string {
  // Route-request → lifecycle pillar (submitted/seen/needs_approval/accepted/blocked)
  if (isRouteRequest(d)) {
    const lc = routeRequestLifecycle(d);
    const pill = 'display:inline-block;font-size:11px;padding:2px 8px;border-radius:10px;font-weight:500;';
    return `<span style="${pill}background:#11203a;color:${LIFECYCLE_TONE[lc]};" title="Hermes → Nexus route-request lifecycle">${escHtml(LIFECYCLE_LABEL[lc])}</span>`;
  }
  if (!isHermesAuthorityDecision(d)) return escHtml(d.status);
  const pill = 'display:inline-block;font-size:11px;padding:2px 8px;border-radius:10px;font-weight:500;';
  if (d.status === 'open') return `<span style="${pill}background:#3a2a1a;color:#fbbf24;">🟠 Council kararı bekliyor</span>`;
  if (d.status === 'rejected' || d.status === 'expired') return `<span style="${pill}background:#4a1f1f;color:#f0a8a8;">🔴 Reddedildi</span>`;
  if (d.status === 'approved') {
    return `<span style="${pill}background:#1f4a2a;color:#9be8a8;" title="Council onayladı — Hermes propose-only, governance yetkisi aktif (DB-grant gerekmez)">🟢 Council-Onaylı (propose-only)</span>`;
  }
  return escHtml(d.status);
}

function summaryStrip(s: CouncilSummary, hermesCount: number = 0): string {
  return `<div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
    <span><strong>${s.total}</strong> total</span>
    ${hermesCount > 0 ? `<span style="color:#9bd0ff;">🛡️ <strong>${hermesCount}</strong> Hermes Yetki</span>` : ''}
    ${chips('status', s.byStatus)}
    ${chips('risk', s.byRiskLevel)}
  </div>`;
}

function chips(label: string, m: Record<string, number>): string {
  const entries = Object.entries(m);
  if (entries.length === 0) return '';
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${entries.map(([k, v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}

function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cnc-f-${id}" class="form-select" style="width:130px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o === value ? ' selected' : ''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cnc-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}

function attachHandlers(): void {
  const btn = document.getElementById('cnc-apply');
  if (btn) {
    btn.addEventListener('click', async () => {
      const getV = (id: string) => (document.getElementById(`cnc-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
      filters = {
        status: getV('status'),
        riskLevel: getV('riskLevel'),
        decisionType: getV('decisionType'),
        tenantId: getV('tenantId'),
        limit: 50,
      };
      await loadAgentCouncil();
      renderAgentCouncil();
    });
  }

  // Stream 6 — Hermes Yetki filter toggle (frontend-side filter, fetch'siz)
  const hermesBtn = document.getElementById('cnc-hermes-toggle');
  if (hermesBtn) {
    hermesBtn.addEventListener('click', () => {
      hermesFilterActive = !hermesFilterActive;
      renderAgentCouncil();
    });
  }

  // Sprint 4 — İnsan-override resolve delegation (Onayla/Reddet).
  const root = document.getElementById('view-agent-council');
  if (root && !(root as HTMLElement & { _cncBound?: boolean })._cncBound) {
    (root as HTMLElement & { _cncBound?: boolean })._cncBound = true;
    root.addEventListener('click', async (ev) => {
      const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-cnc-resolve]');
      if (!target) return;
      const id = target.dataset.cncId;
      const decision = target.dataset.cncResolve as 'approved' | 'rejected' | undefined;
      if (!id || (decision !== 'approved' && decision !== 'rejected')) return;
      const risk = target.dataset.cncRisk ?? '';
      const title = target.dataset.cncTitle ?? '';
      const verb = decision === 'approved' ? 'ONAYLA' : 'REDDET';
      const ok = window.confirm(
        `Council kararını ${verb}?\n\n${title}\nRisk: ${risk}\n\nBu aksiyon kararı ANINDA çözer (eşikten bağımsız) ve geri alınamaz.`,
      );
      if (!ok) return;
      const r = await postCouncilResolve(id, decision);
      if (r.ok) {
        toast(decision === 'approved' ? 'Karar onaylandı' : 'Karar reddedildi', 'success');
        await loadAgentCouncil();
        renderAgentCouncil();
      } else {
        const code = r.status === 403 ? 'yetki yok (staff/admin gerekir)' : r.status === 409 ? 'karar zaten çözülmüş' : `HTTP ${r.status}`;
        toast(`Resolve başarısız: ${code}`, 'error');
      }
    });
  }
}
